import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AppShell } from "./components/app-shell";
import { ComparePage, compareSearchSchema } from "./routes/compare";
import {
  ShipsPage,
  StructuresPage,
  shipsSearchSchema,
  structuresSearchSchema,
} from "./routes/roster";
import { TechPage, techSearchSchema } from "./routes/tech";
import { UnitPage, unitSearchSchema } from "./routes/unit";

// Code-based routing, no codegen (keylens ADR 0001 amendment: the router plugin is
// Vite-coupled; hand-assembling the route tree keeps the toolchain all-Bun).
const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
  notFoundComponent: () => (
    <div className="py-16 text-center text-[15px] text-ink-3">Not found.</div>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    // Ships is the default view (design language); / stays free for a future landing.
    throw redirect({ to: "/ships" });
  },
});

// The two views over the Roster (ADR 0008): /ships and /structures, per routes/roster.tsx.
const shipsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ships",
  validateSearch: shipsSearchSchema,
  component: ShipsPage,
});

const structuresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/structures",
  validateSearch: structuresSearchSchema,
  component: StructuresPage,
});

// /roster was the one roster's address before the split; links to it are in the wild and
// keep working (ADR 0002). The search is passed through UNVALIDATED so the whole query
// string arrives at /ships, which then applies its own schema — validating here would
// strip the keys this route never knew about before /ships had a chance to report them.
const rosterRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/roster",
  validateSearch: (search: Record<string, unknown>) => search,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/ships", search: search as never });
  },
});

// Entity pages (ADR 0002): /unit/<stable game id>, e.g. /unit/trader_antiarmor_frigate.
// Unknown ids render an in-page explanation with the dataset's game version — a shared link
// can outlive a unit across patches. ?tech= selects the research half of the stat frame.
const unitRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/unit/$unitId",
  validateSearch: unitSearchSchema,
  component: UnitPage,
});

// The tech-tree browser (Issue 13): /tech?faction=…&domain=… per lib/tech-filters.ts.
const techRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tech",
  validateSearch: techSearchSchema,
  component: TechPage,
});

// The comparison view (Issue 07): /compare?units=<id>,<id>,…&tech=… per lib/compare.ts.
const compareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/compare",
  validateSearch: compareSearchSchema,
  component: ComparePage,
});

/** The whole tree, exported so a test can mount it on a memory history and prove the
 * /roster redirect keeps every query key. */
export const routeTree = rootRoute.addChildren([
  indexRoute,
  shipsRoute,
  structuresRoute,
  rosterRedirectRoute,
  unitRoute,
  techRoute,
  compareRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
